import { widgetTypeSchema } from '@teo/types';
import { z } from 'zod';
import { calendarStatusSchema } from './calendar.js';
import { displayId, entityInputSchema } from './common.js';
export const relativeDateRangeSchema = z
    .object({
    pastMonths: z
        .number()
        .int()
        .min(0)
        .max(24)
        .default(1)
        .describe('How many months before today it shows.'),
    futureMonths: z
        .number()
        .int()
        .min(0)
        .max(24)
        .default(3)
        .describe('How many months after today it shows.'),
})
    .describe('The months the widget covers, counted from today.');
export const widgetPreferredViewSchema = z.enum(['chart', 'list']);
export const chartOnlyWidgetPreferredViewSchema = z.literal('chart');
export const DASHBOARD_WIDGET_CONFIG_TYPES = [
    'calendar',
    'invoice',
    'budget',
    'financial-health',
    'operating-income',
    'operating-expenses',
    'unit',
    'receivable',
    'payable',
    'rent-radar',
];
const DASHBOARD_WIDGET_SUPPORTED_VIEWS = {
    calendar: ['chart', 'list'],
    invoice: ['chart', 'list'],
    budget: ['chart', 'list'],
    'financial-health': ['chart'],
    'operating-income': ['chart'],
    'operating-expenses': ['chart'],
    unit: ['chart', 'list'],
    receivable: ['chart', 'list'],
    payable: ['chart', 'list'],
    'rent-radar': ['chart'],
};
export function isDashboardWidgetConfigType(widgetType) {
    return (typeof widgetType === 'string' &&
        DASHBOARD_WIDGET_CONFIG_TYPES.some((dashboardWidgetType) => dashboardWidgetType === widgetType));
}
export function resolveSupportedDashboardWidgetViews(widgetType) {
    return DASHBOARD_WIDGET_SUPPORTED_VIEWS[widgetType];
}
export function isSupportedDashboardWidgetView(widgetType, preferredView) {
    const parsed = widgetPreferredViewSchema.safeParse(preferredView);
    if (!parsed.success) {
        return false;
    }
    return resolveSupportedDashboardWidgetViews(widgetType).includes(parsed.data);
}
export const calendarWidgetConfigSchema = z.object({
    widgetType: z.literal('calendar'),
    entityInput: entityInputSchema.optional(),
    types: z
        .array(z.string())
        .optional()
        .describe('Show only these kinds of calendar entry. Omit for every kind.'),
    status: calendarStatusSchema.default('all'),
    search: z.string().optional().describe('Show only entries whose text has this. Omit for all.'),
    includeDismissed: z.boolean().default(false).describe('Also show the dismissed entries.'),
    hideCompleted: z.boolean().default(false).describe('Leave out the completed entries.'),
    dateRange: relativeDateRangeSchema,
    span: z
        .number()
        .int()
        .min(1)
        .max(3)
        .default(1)
        .describe('How many columns wide the widget is, 1 to 3.'),
    preferredView: widgetPreferredViewSchema
        .default('chart')
        .describe('Show it as a chart or as a list.'),
});
export const invoiceWidgetConfigSchema = z.object({
    widgetType: z.literal('invoice'),
    entityInput: entityInputSchema.optional(),
    span: z
        .number()
        .int()
        .min(1)
        .max(3)
        .default(1)
        .describe('How many columns wide the widget is, 1 to 3.'),
    preferredView: widgetPreferredViewSchema
        .default('list')
        .describe('Show it as a chart or as a list.'),
});
export const budgetWidgetConfigSchema = z.object({
    widgetType: z.literal('budget'),
    entityInput: entityInputSchema.optional(),
    span: z
        .number()
        .int()
        .min(1)
        .max(3)
        .default(2)
        .describe('How many columns wide the widget is, 1 to 3.'),
    preferredView: widgetPreferredViewSchema
        .default('chart')
        .describe('Show it as a chart or as a list.'),
});
export const unitWidgetConfigSchema = z.object({
    widgetType: z.literal('unit'),
    entityInput: entityInputSchema.optional(),
    span: z
        .number()
        .int()
        .min(1)
        .max(3)
        .default(1)
        .describe('How many columns wide the widget is, 1 to 3.'),
    preferredView: widgetPreferredViewSchema
        .default('chart')
        .describe('Show it as a chart or as a list.'),
});
export const receivableWidgetConfigSchema = z.object({
    widgetType: z.literal('receivable'),
    entityInput: entityInputSchema.optional(),
    span: z
        .number()
        .int()
        .min(1)
        .max(3)
        .default(1)
        .describe('How many columns wide the widget is, 1 to 3.'),
    preferredView: widgetPreferredViewSchema
        .default('chart')
        .describe('Show it as a chart or as a list.'),
});
export const payableWidgetConfigSchema = z.object({
    widgetType: z.literal('payable'),
    entityInput: entityInputSchema.optional(),
    span: z
        .number()
        .int()
        .min(1)
        .max(3)
        .default(1)
        .describe('How many columns wide the widget is, 1 to 3.'),
    preferredView: widgetPreferredViewSchema
        .default('chart')
        .describe('Show it as a chart or as a list.'),
});
export const financialHealthWidgetConfigSchema = z.object({
    widgetType: z.literal('financial-health'),
    entityInput: entityInputSchema.optional(),
    span: z.literal(1).default(1).describe('The widget is one column wide.'),
    preferredView: chartOnlyWidgetPreferredViewSchema
        .default('chart')
        .describe('This widget shows only as a chart.'),
});
export const rentRadarWidgetConfigSchema = z.object({
    widgetType: z.literal('rent-radar'),
    span: z
        .number()
        .int()
        .min(1)
        .max(3)
        .default(2)
        .describe('How many columns wide the widget is, 1 to 3.'),
    preferredView: chartOnlyWidgetPreferredViewSchema
        .default('chart')
        .describe('This widget shows only as a chart.'),
});
export const operatingExpensesWidgetConfigSchema = z.object({
    widgetType: z.literal('operating-expenses'),
    entityInput: entityInputSchema.optional(),
    span: z.literal(1).default(1).describe('The widget is one column wide.'),
    preferredView: chartOnlyWidgetPreferredViewSchema
        .default('chart')
        .describe('This widget shows only as a chart.'),
});
export const operatingIncomeWidgetConfigSchema = z.object({
    widgetType: z.literal('operating-income'),
    entityInput: entityInputSchema.optional(),
    span: z.literal(1).default(1).describe('The widget is one column wide.'),
    preferredView: chartOnlyWidgetPreferredViewSchema
        .default('chart')
        .describe('This widget shows only as a chart.'),
});
export const widgetConfigSchema = z.discriminatedUnion('widgetType', [
    calendarWidgetConfigSchema,
    invoiceWidgetConfigSchema,
    budgetWidgetConfigSchema,
    financialHealthWidgetConfigSchema,
    operatingIncomeWidgetConfigSchema,
    operatingExpensesWidgetConfigSchema,
    unitWidgetConfigSchema,
    receivableWidgetConfigSchema,
    payableWidgetConfigSchema,
    rentRadarWidgetConfigSchema,
]);
export const createDashboardWidgetSchema = z.object({
    title: z.string().min(1).max(255).describe('The title the widget shows.'),
    config: widgetConfigSchema.describe('The widget type and its settings.'),
    position: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('Where it goes, from 0 for first. Omit to put it last.'),
    bootstrapDefaults: z
        .boolean()
        .optional()
        .describe('Only for the first visit, which makes the default set. Leave it out.'),
});
export const deleteDashboardWidgetSchema = z.object({
    widgetId: displayId,
});
export const reorderDashboardWidgetsSchema = z.object({
    widgetIds: z.array(displayId).min(1).describe('The widget ids, first to last.'),
});
export const setWidgetVisibilitySchema = z.object({
    widgetId: displayId,
    isVisible: z.boolean().describe('true to show the widget, false to hide it.'),
});
export const updateWidgetTitleSchema = z.object({
    widgetId: displayId,
    title: z.string().trim().min(1).max(255).describe('The new title.'),
});
export const updateWidgetPreferredViewSchema = z.object({
    widgetId: displayId,
    preferredView: widgetPreferredViewSchema.describe('Show it as a chart or as a list.'),
});
export const dashboardWidgetOutputSchema = z.object({
    displayId: z.string(),
    widgetType: widgetTypeSchema,
    title: z.string(),
    position: z.number(),
    isVisible: z.boolean(),
    config: widgetConfigSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const getDashboardWidgetsOutputSchema = z.object({
    widgets: z.array(dashboardWidgetOutputSchema),
});
//# sourceMappingURL=dashboard.js.map